import axios, { AxiosResponse } from 'axios';
import { CONFIG } from '../config/config';
import { getToken } from './auth';
import { review } from '../protos/compiled';
// Import the UI component's ReviewQuestion type
import { ReviewQuestion as ComponentReviewQuestion } from '../types';

const { ReviewEntity: ProtoReviewEntity, ReviewEntityList, ReviewQuestion: ProtoReviewQuestion, ReviewQuestionList } = review;

type ApiValidationError = {
    msg?: string;
    loc?: Array<string | number>;
};

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
    const axiosError = error as {
        response?: { data?: { detail?: unknown; message?: unknown } };
        message?: unknown;
    };
    const detail = axiosError.response?.data?.detail;
    const message = axiosError.response?.data?.message ?? axiosError.message;

    if (typeof detail === 'string') {
        return detail;
    }

    if (Array.isArray(detail)) {
        const validationMessages = detail
            .map((item: ApiValidationError | unknown) => {
                if (item && typeof item === 'object' && 'msg' in item) {
                    const validationError = item as ApiValidationError;
                    const field = validationError.loc?.filter(part => part !== 'body').join('.');
                    return field ? `${field}: ${validationError.msg}` : validationError.msg;
                }
                return typeof item === 'string' ? item : null;
            })
            .filter((item): item is string => Boolean(item));

        if (validationMessages.length > 0) {
            return validationMessages.join('\n');
        }
    }

    if (detail && typeof detail === 'object' && 'msg' in detail) {
        const validationError = detail as ApiValidationError;
        const field = validationError.loc?.filter(part => part !== 'body').join('.');
        return field ? `${field}: ${validationError.msg}` : validationError.msg || fallback;
    }

    if (message && typeof message === 'string') {
        return message;
    }

    return fallback;
};

// Type adapters to convert between API and component types
const adaptToComponentReviewQuestion = (apiQuestion: ReviewQuestion): ComponentReviewQuestion => {
    // Map from API type to UI Component type with all required fields
    const componentQuestion: any = {
        id: apiQuestion.id,
        review_question_text: apiQuestion.review_question_text || '',
        review_entity_type: apiQuestion.review_entity_type || '',
        description: apiQuestion.description || '',
        review_dimension: apiQuestion.review_dimension || '',
        review_frequency: apiQuestion.review_frequency || '',
        answer_type: apiQuestion.answer_type || '',
        answer_options: Array.isArray(apiQuestion.answer_options) ? apiQuestion.answer_options : [],
        review_question_type: apiQuestion.review_question_type || '',
        action_required: apiQuestion.action_required || '',
        // Use the values from the API response if available
        default_value: apiQuestion.default_value || '',
        ideal_value: apiQuestion.ideal_value || '',
        created_by: '', // String type expected by component
        created_at: '',
        updated_at: '',
        updated_by: '' // String type expected by component
    };
    
    // Return as ComponentReviewQuestion type with safe cast
    return componentQuestion as ComponentReviewQuestion;
};

// Convert a list of API questions to component questions
const adaptQuestionList = (apiQuestions: ReviewQuestion[]): ComponentReviewQuestion[] => {
    if (!apiQuestions || !Array.isArray(apiQuestions)) return [];
    return apiQuestions.map(question => adaptToComponentReviewQuestion(question));
};

// Function to convert a list of API entities (now just returns them as-is)
export const adaptEntityList = (apiEntities: ReviewEntity[]): ReviewEntity[] => {
    return apiEntities;
};

// Adapter functions to convert protobuf interfaces to frontend interfaces
const adaptProtoEntityToFrontend = (protoEntity: review.IReviewEntity): ReviewEntity => {
  // DETAILED DEBUG: Log the exact protobuf entity received before any processing
  console.log('PROTOBUF RAW ENTITY:', JSON.stringify(protoEntity));
  
  // Extract the fields we need from the protobuf entity, providing safe fallbacks
  const id = protoEntity.id || 0;
  const name = protoEntity.name || '';
  const description = protoEntity.description || '';
  
  // Add org_entity_code (now available in protobuf)
  const orgEntityCode = protoEntity.orgEntityCode || '';
  
  // CRITICAL: Extract entity type fields for display
  // This is what shows up in the Type column of the UI
  const reviewEntityTypeId = typeof protoEntity.reviewEntityType === 'number' ? protoEntity.reviewEntityType : 0;
  
  // DEBUG: Log type fields specifically
  console.log('TYPE FIELD CHECK:');
  console.log('- reviewEntityTypeId:', reviewEntityTypeId);
  
  // Handle reviewer/reviewee IDs with fallbacks
  const reviewerIds = Array.isArray(protoEntity.reviewerIds) ? protoEntity.reviewerIds : [];
  const revieweeIds = Array.isArray(protoEntity.revieweeIds) ? protoEntity.revieweeIds : [];
  
  // Handle reviewer/reviewee names - these populate the chips in the UI
  const reviewerNames = Array.isArray(protoEntity.reviewerNames) ? protoEntity.reviewerNames : [];
  const revieweeNames = Array.isArray(protoEntity.revieweeNames) ? protoEntity.revieweeNames : [];
  
  // Handle parent IDs
  const parentIds: number[] = Array.isArray(protoEntity.parentIds) ? protoEntity.parentIds : [];
  
  
  // Handle attributes - now available in protobuf
  let attributesList: ReviewAttribute[] = [];
  if (Array.isArray(protoEntity.attributes)) {
    attributesList = protoEntity.attributes.map((attr: review.IReviewEntityAttribute) => ({
      id: 0, // Default since proto doesn't have ID
      name: attr.name || '',
      description: attr.description || '',
      value: attr.value || '',
      review_entity_id: id
    }));
    console.log('Found attributes in protobuf:', attributesList.length);
  }
  
  // Create an empty parents array (not available in protobuf)
  const parentsList: {id: number; name: string; review_entity_type_id: number}[] = [];
  
  // Handle metadata fields - now available in protobuf
  const createdBy = protoEntity.createdBy || 0;
  const updatedBy = protoEntity.updatedBy || 0;
  const createdAt = protoEntity.createdAt || new Date().toISOString();
  const updatedAt = protoEntity.updatedAt || new Date().toISOString();

  // DEBUG: Log important fields to verify they're properly populated
  console.log('IMPORTANT FIELDS:');
  console.log('- OrgEntityCode:', orgEntityCode);
  console.log('- Type ID:', reviewEntityTypeId);
  console.log('- Reviewers:', reviewerNames);
  console.log('- Reviewees:', revieweeNames);
  console.log('- Attributes:', attributesList.length);
  console.log('- Metadata:', { createdBy, updatedBy, createdAt, updatedAt });

  // Create the frontend entity with proper field mapping
  const frontendEntity: ReviewEntity = {
    id,
    name,
    description,
    org_entity_code: orgEntityCode,
    review_entity_type_id: reviewEntityTypeId,
    reviewers: reviewerIds,
    reviewees: revieweeIds,
    reviewer_names: reviewerNames,
    reviewee_names: revieweeNames,
    attributes: attributesList,
    review_attributes: attributesList,
    parents: parentsList,
    parent_ids: parentIds,
    created_by: createdBy,
    updated_by: updatedBy,
    created_at: createdAt,
    updated_at: updatedAt
  };
  
  // Log the final converted entity to verify all fields
  console.log('FINAL CONVERTED ENTITY:', frontendEntity);
  
  return frontendEntity;
};

const adaptProtoQuestionToFrontend = (protoQuestion: review.IReviewQuestion): ReviewQuestion => {
  // Extract base fields with safe fallbacks
  const id = protoQuestion.id || 0;
  const reviewQuestionText = protoQuestion.reviewQuestionText || '';
  const description = protoQuestion.description || '';
  const reviewFrequency = protoQuestion.reviewFrequency || '';
  const reviewEntityType = protoQuestion.reviewEntityType || '';
  const reviewDimension = protoQuestion.reviewDimension || '';
  
  // Use type assertion for enum types
  const answerType = (protoQuestion.answerType || 'TEXT') as "YES-NO" | "TEXT" | "SELECT" | "MULTI-SELECT";
  
  // Handle arrays safely
  const answerOptions = Array.isArray(protoQuestion.answerOptions) ? protoQuestion.answerOptions : [];
  const reviewQuestionType = protoQuestion.reviewQuestionType || '';
  
  // Convert boolean to string if needed
  const actionRequired = protoQuestion.actionRequired ? "true" : "false";
  
  return {
    id,
    review_question_text: reviewQuestionText,
    description,
    review_frequency: reviewFrequency,
    review_entity_type: reviewEntityType,
    review_dimension: reviewDimension,
    answer_type: answerType,
    answer_options: answerOptions,
    review_question_type: reviewQuestionType,
    action_required: actionRequired,
    // Proto doesn't have these fields yet, so handle as empty strings
    // This will need to be updated when the frontend proto files are regenerated
    default_value: '',
    ideal_value: ''
  };
};

// Protocol Buffer support
const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
};

// Function to determine if we should use protobuf for an endpoint
const shouldUseProtobuf = (endpoint: string): boolean => {
    // Currently only enable for review questions and entities
    return endpoint.includes('/review_questions') || endpoint.includes('/review_entities');
};

// Generic function to handle both protobuf and JSON responses with proper type conversion
const fetchWithProtobufSupport = async <T>(endpoint: string, params?: any): Promise<AxiosResponse<T[]>> => {
    if (shouldUseProtobuf(endpoint)) {
        try {
            // Try protobuf first
            const response = await protobufApi.get(endpoint, {
                params,
                responseType: 'arraybuffer'
            });

            // Handle protobuf response
            if (response.headers['content-type'] === 'application/x-protobuf') {
                const buffer = new Uint8Array(response.data);
                let decoded;
                
                try {
                    // Handle review questions
                    if (endpoint.includes('/review_questions')) {
                        decoded = review.ReviewQuestionList.decode(buffer);
                        const protoQuestions = decoded.questions ? Array.from(decoded.questions) : [];
                        const apiQuestions = protoQuestions.map(question => adaptProtoQuestionToFrontend(question));
                        const componentQuestions = adaptQuestionList(apiQuestions);
                        return { ...response, data: componentQuestions as unknown as T[] };
                    } 
                    // Handle review entities
                    else if (endpoint.includes('/review_entities') || endpoint.includes('/review_entity')) {
                        decoded = review.ReviewEntityList.decode(buffer);
                        const protoEntities = decoded.entities ? Array.from(decoded.entities) : [];
                        const apiEntities = protoEntities.map(entity => adaptProtoEntityToFrontend(entity));
                        const componentEntities = adaptEntityList(apiEntities);
                        return { ...response, data: componentEntities as unknown as T[] };
                    }
                    // Generic handling for other types
                    else {
                        console.warn('No specific protobuf handler for endpoint:', endpoint);
                        return { ...response, data: [] as unknown as T[] };
                    }
                } catch (decodeError) {
                    console.error('Error decoding protobuf:', decodeError);
                    // Fall back to JSON on protobuf decode error
                    const jsonFallback = await api.get<any[]>(endpoint, { params });
                    return handleJsonResponse(jsonFallback, endpoint);
                }
            }

            // Handle JSON response from protobuf API (fallback)
            if (response.headers['content-type']?.includes('application/json')) {
                try {
                    const textDecoder = new TextDecoder();
                    const jsonText = textDecoder.decode(response.data);
                    const jsonData = JSON.parse(jsonText);
                    return handleJsonData(response, jsonData, endpoint);
                } catch (jsonError) {
                    console.error('Error parsing JSON from protobuf response:', jsonError);
                    // Fall back to standard JSON API
                    const standardJson = await api.get(endpoint, { params });
                    return handleJsonResponse(standardJson, endpoint);
                }
            }

            // Unknown content type
            console.warn('Unknown content type:', response.headers['content-type']);
            return { ...response, data: [] as unknown as T[] };
        } 
        catch (error) {
            console.error('Error in protobuf request, falling back to JSON:', error);
            // Fall back to JSON API on any error
            const jsonResponse = await api.get<any[]>(endpoint, { params });
            return handleJsonResponse(jsonResponse, endpoint);
        }
    }
    
        // Standard JSON API call (protobuf not enabled)
    console.log('Using standard JSON API for:', endpoint);
    const jsonResponse = await api.get<any[]>(endpoint, { params });
    return handleJsonResponse<T>(jsonResponse, endpoint);
};

// Helper function to handle JSON responses
const handleJsonResponse = <T>(response: AxiosResponse<any[]>, endpoint: string): AxiosResponse<T[]> => {
    if (endpoint.includes('/review_questions')) {
        const componentData = adaptQuestionList(response.data);
        return { ...response, data: componentData as unknown as T[] };
    } 
    else if (endpoint.includes('/review_entities') || endpoint.includes('/review_entity')) {
        const componentData = adaptEntityList(response.data);
        return { ...response, data: componentData as unknown as T[] };
    }
    return response;
};

// Helper function to handle JSON data from arraybuffer response
const handleJsonData = <T>(response: AxiosResponse<any>, jsonData: any, endpoint: string): AxiosResponse<T[]> => {
    if (endpoint.includes('/review_questions')) {
        const componentData = adaptQuestionList(jsonData);
        return { ...response, data: componentData as unknown as T[] };
    } 
    else if (endpoint.includes('/review_entities') || endpoint.includes('/review_entity')) {
        const componentData = adaptEntityList(jsonData);
        return { ...response, data: componentData as unknown as T[] };
    }
    return { ...response, data: jsonData };
};

// API Types
export interface ReviewEntityType {
    id: number;
    name: string;
    display_label: string;
    category: string;
    description: string;
    associated_review_dimensions?: number[];
    associated_review_dimension_names?: string[];
}

export interface ReviewFrequency {
    id: number;
    name: string;
    display_label: string;
    description: string;
}

export interface ReviewDimension {
    id: number;
    name: string;
    description: string;
    created_by?: string | number;
    updated_by?: string | number;
    created_at?: string;
    updated_at?: string;
}

export interface ReviewQuestion {
    id: number;
    review_question_text: string;
    description: string;
    review_dimension?: string | {
        id: number;
        name: string;
        description: string;
        created_by: number;
        updated_by: number;
        created_at: string;
        updated_at: string;
    };
    review_dimension_id?: number;
    review_frequency?: string;
    review_entity_type?: string;
    review_definition_id?: number;
    review_entity_type_id?: number;
    answer_type: 'YES-NO' | 'TEXT' | 'SELECT' | 'MULTI-SELECT' | 'NUMERIC' | 'PERCENTAGE';
    answer_options?: string[];
    review_question_type: string;
    action_required: string;
    default_value?: string;
    ideal_value?: string;
}

export interface ReviewQuestionAnswer {
    id: number;
    review_question_id: number;
    review_questionnaire_response_id: number;
    answer_value: string[];
    submitted_by: number;
    submitted_by_name: {
        id: number;
        user_name: string;
        roles: string[];
    };
}

const api = axios.create({
    baseURL: CONFIG.SERVER_URL
});

// Protobuf-enabled API client
const protobufApi = axios.create({
    baseURL: CONFIG.SERVER_URL,
    responseType: 'arraybuffer',
    headers: {
        'Accept': 'application/x-protobuf'
    }
});

// Same origin as `api` (includes REACT_APP_API_PREFIX in production, e.g. /api).
// Using only REACT_APP_API_URL omitted the prefix and broke /scheduler/*, /compute-progress, etc.
const directApi = axios.create({
    baseURL: CONFIG.SERVER_URL
});

api.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

protobufApi.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

directApi.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle session timeout
const handleSessionTimeout = () => {
    // Find the session timeout context and show the dialog
    const timeoutEvent = new CustomEvent('sessionTimeout');
    window.dispatchEvent(timeoutEvent);
};

// Add response interceptor
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            handleSessionTimeout();
        }
        return Promise.reject(error);
    }
);

// Add response interceptor for protobuf API
protobufApi.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            handleSessionTimeout();
        }
        return Promise.reject(error);
    }
);

export const getReviewEntityTypes = (): Promise<AxiosResponse<ReviewEntityType[]>> =>
    api.get('/review_entity_types');

export interface ReviewType {
    id: number;
    name: string;
    display_label: string;
    description: string;
}

export interface ReviewDefinition {
    id: number;
    name: string;
    display_label: string;
    description: string;
    review_type_id: number;
    review_frequency_id: number;
    submission_overdue_days: number;
    closure_overdue_days: number;
    applicable_review_entity_type_ids: number[];
    created_by: number | string;
    updated_by: number | string;
    created_at?: string;
    updated_at?: string;
    review_type?: ReviewType;
    review_frequency?: ReviewFrequency;
    applicable_review_entity_types?: ReviewEntityType[];
}

export interface CreateReviewDefinitionPayload {
    name: string;
    display_label: string;
    description: string;
    review_type_id: number;
    review_frequency_id?: number;  // Optional - only required for PERIODIC review types
    submission_overdue_days: number;
    closure_overdue_days: number;
    applicable_review_entity_types: number[];
}

export interface UpdateReviewDefinitionPayload extends CreateReviewDefinitionPayload {
    id: number;
}

export const getReviewDefinitions = (): Promise<AxiosResponse<ReviewDefinition[]>> =>
    api.get('/review_definitions');

export const getReviewTypes = (): Promise<AxiosResponse<ReviewType[]>> =>
    api.get('/review_types');

export const createReviewDefinition = (data: CreateReviewDefinitionPayload): Promise<AxiosResponse<ReviewDefinition>> =>
    api.post('/review_definition', data);

export const updateReviewDefinition = (data: UpdateReviewDefinitionPayload): Promise<AxiosResponse<ReviewDefinition>> =>
    api.put('/review_definition', data);

export const deleteReviewDefinition = (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/review_definition/${id}`);

// Return component types for UI compatibility
export const getReviewQuestions = async (entityName?: string): Promise<AxiosResponse<ComponentReviewQuestion[]>> => {
    const endpoint = entityName ? 
        `/review_questions/${encodeURIComponent(entityName)}` : 
        '/review_questions';
    
    // Get API response
    const response = await fetchWithProtobufSupport<ReviewQuestion>(endpoint);
    
    // Convert API response to component type
    const componentData = adaptQuestionList(response.data);
    return { ...response, data: componentData };
};

export const getEntities = (entityType: string) =>
    api.get(`/entities?entity_type=${entityType}`);

export const createReviewQuestion = (questionData: {
    review_question_text: string;
    description: string;
    review_dimension: string;
    review_frequency: string;
    answer_type: string;
    answer_options?: string[];
    review_question_type: string;
    review_entity_type: string;  // This should be the 'name' field from ReviewEntityType
    action_required: string;
}) => api.post('/review_questions', {
    ...questionData,
    review_entity_type: encodeURIComponent(questionData.review_entity_type)
});

export interface AvailableQuestionnaire {
    review_definition_id: number;
    review_entity_type_id: number;
    review_definition: ReviewDefinition;
    review_entity_type: ReviewEntityType;
    question_count: number;
}

export const getAvailableQuestionnaires = (): Promise<AxiosResponse<AvailableQuestionnaire[]>> =>
    api.get('/available-questionnaires');

export const getQuestionsByDefinitionAndEntityType = (
    reviewDefinitionId: number,
    reviewEntityTypeId: number
): Promise<AxiosResponse<ReviewQuestion[]>> =>
    api.get(`/review_questions/review_definition/${reviewDefinitionId}/review_entity_type/${reviewEntityTypeId}`);

export const addNewReviewQuestion = (questionData: {
    review_question_text: string;
    description: string;
    review_dimension_id: number;
    review_definition_id: number;
    review_entity_type_id: number;
    answer_type: string;
    review_question_type: string;
    review_entity_type: string;
    answer_options?: string[];
    default_value?: string;
    ideal_value?: string;
    action_required?: string;
}): Promise<AxiosResponse<any>> =>
    api.post('/review_questions', questionData);

export const uploadQuestionnaire = (formData: FormData) => 
    directApi.post('/upload-questionnaire', formData, {
        timeout: 180000 // 3 minutes timeout
    });

export const uploadMetrics = (formData: FormData) => 
    directApi.post('/upload-metrics', formData, {
        timeout: 180000 // 3 minutes timeout
    });

export const getUploadProgress = (processId?: number, fileName?: string) =>
    directApi.get('/upload-progress', {
        params: processId ? { process_id: processId } : (fileName ? { file_name: fileName } : {})
    });

export const getComputeProgress = (processId?: number, fileName?: string) =>
    directApi.get('/compute-progress', {
        params: processId ? { process_id: processId } : (fileName ? { file_name: fileName } : {})
    });

export const getUploadHistory = () =>
    directApi.get('/upload-history');

export const getComputeHistory = () =>
    directApi.get('/compute-history');

export type MetricsReportFormat = 'xlsx' | 'csv';

const downloadMetricsReportBlob = async (
    endpoint: string,
    processId: number,
    format: MetricsReportFormat,
    fallbackFilename: string,
): Promise<void> => {
    try {
        const response = await directApi.get(endpoint, {
            params: { process_id: processId, file_format: format },
            responseType: 'blob',
        });
        const blob =
            response.data instanceof Blob
                ? response.data
                : new Blob([response.data], {
                      type: format === 'csv'
                          ? 'text/csv;charset=utf-8;'
                          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                  });
        if (!blob.size) {
            throw new Error('Empty report response.');
        }
        const disposition = response.headers['content-disposition'] as string | undefined;
        const filenameMatch = disposition?.match(/filename="?([^"]+)"?/i);
        const filename = filenameMatch?.[1] || fallbackFilename;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
        if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
            const text = await error.response.data.text();
            let msg: string | undefined;
            try {
                const parsed = JSON.parse(text) as { detail?: unknown };
                const detail = parsed.detail;
                msg =
                    typeof detail === 'string'
                        ? detail
                        : Array.isArray(detail)
                          ? JSON.stringify(detail)
                          : undefined;
            } catch {
                msg = text || undefined;
            }
            throw new Error(msg || 'Failed to download report.');
        }
        throw error;
    }
};

export const downloadUploadMetricsReport = (
    processId: number,
    format: MetricsReportFormat = 'xlsx',
): Promise<void> =>
    downloadMetricsReportBlob(
        '/upload-report/download',
        processId,
        format,
        `metrics_upload_report_${processId}.${format}`,
    );

export const downloadComputeMetricsReport = (
    processId: number,
    format: MetricsReportFormat = 'xlsx',
): Promise<void> =>
    downloadMetricsReportBlob(
        '/compute-report/download',
        processId,
        format,
        `metrics_compute_report_${processId}.${format}`,
    );

// Check if a value meets the ideal value criteria for NUMERIC or PERCENTAGE answer types
export interface CheckIdealValueRequest {
    ideal_value: string;
    actual_value: string;
    answer_type: 'NUMERIC' | 'PERCENTAGE';
}

export interface CheckIdealValueResponse {
    is_ideal: boolean;
    ideal_value: string;
    actual_value: string;
    suggested_value: string | null;
}

export const checkIdealValue = (data: CheckIdealValueRequest): Promise<AxiosResponse<CheckIdealValueResponse>> =>
    api.post('/check-ideal-value', data);

// Utility functions for direct API access (bypassing /api prefix)
// Use these when browser interceptors interfere with /api prefixed calls
export const directApiGet = (url: string, config?: any) => directApi.get(url, config);
export const directApiPost = (url: string, data?: any, config?: any) => directApi.post(url, data, config);
export const directApiPut = (url: string, data?: any, config?: any) => directApi.put(url, data, config);
export const directApiDelete = (url: string, config?: any) => directApi.delete(url, config);

// User Management APIs
export const addUser = (userData: { user_name: string; password: string }) => 
    api.post('/adduser', userData);

export const addDefaultAuthUser = (userData: {
    user_name: string;
    password: string;
    email: string;
    display_name?: string;
    status?: string;
    attributes?: { [key: string]: any };
    preferences?: { [key: string]: string };
}) => api.post('/add_default_auth_user', userData);

export interface User {
    id: number;
    user_name: string;
    email: string;
    display_name: string;
    external_id?: string;
    upn?: string;
    attributes?: { [key: string]: any };
    preferences?: { [key: string]: string };
    status: string;
    created_at: string;
    updated_at: string;
    last_login_at?: string;
    roles: Array<{
        role_id: number;
        role_name: string;
    }>;
}

export const getUsers = () => api.get('/users');

export const getRoles = () => api.get('/entities', {
    params: { entity_type: 'AppRole' }
});

export const addRole = (payload: { user_id: number; role_id: number }) => 
    api.post('/addrole', payload);

export const revokeRole = (payload: { user_id: number; role_id: number }) => 
    api.post('/revokerole', payload);

export const deleteUser = (userName: string) => 
    api.delete(`/deleteuser/${encodeURIComponent(userName)}`);

export const updateUser = (userData: {
    user_id: number;
    user_name: string;
    password?: string;
    email: string;
    display_name?: string;
    status?: string;
    attributes?: { [key: string]: any };
    preferences?: { [key: string]: string };
}) => api.post('/update_default_auth_user', userData);

export const getRolePermissions = () => api.get('/rolepermissions');

export const getAllowedUserPreferences = () =>
    api.get<{ allowed_user_preferences: string[]; value_options_by_key: Record<string, string[]> }>(
        '/allowed_user_preferences'
    );

export const getAllowedUserAttributes = () =>
    api.get<{ allowed_user_attributes: string[] }>('/allowed_user_attributes');

/** Current user's preferences (same data as in JWT after login; use after updates via `updateMyPreferences`). */
export const getMyPreferences = () =>
    api.get<{ preferences: Record<string, string> }>('/my_preferences');

/** Update current user's preferences; response includes a new JWT with updated `preferences` claims. */
export const updateMyPreferences = (preferences: Record<string, string>) =>
    api.post<{ access_token: string; token_type: string }>('/update_my_preferences', {
        preferences,
    });

/** Change the signed-in user's password. Same SHA-512 storage as Edit User, scoped to the caller. */
export const changeMyPassword = (payload: { current_password: string; new_password: string }) =>
    api.post<string>('/change_my_password', payload);

// Review Entity Management APIs
export interface ReviewEntity {
    id: number;
    name: string;
    description: string;
    org_entity_code?: string;
    review_entity_type_id: number;
    review_entity_type?: {
        id: number;
        name: string;
        display_label: string;
        category: string;
        description?: string;
        associated_review_dimensions?: number[];
        associated_review_dimension_names?: string[];
        applicable_metric_levels?: number[];
        metric_level_names?: string[];
        applicable_metric_level_details?: Array<{
            id: number;
            name: string;
            display_label: string;
            is_period_based: boolean;
            level_number: number;
        }>;
    };
    reviewers: number[];
    reviewees: number[];
    data_entry_supports?: number[];
    owner?: number;
    owner_name?: string;
    owner_display_name?: string;
    status?: string;
    valid_from?: string;
    valid_till?: string;
    created_by: number;
    updated_by: number;
    created_at: string;
    updated_at: string;
    // Using the new attributes structure from the refactored backend
    attributes: ReviewAttribute[];
    // Keep review_attributes for backward compatibility with existing code
    review_attributes?: ReviewAttribute[];
    metric_level_config?: any;
    reviewer_names: string[];
    reviewee_names: string[];
    data_entry_support_names?: string[];
    parents?: {
        id: number;
        name: string;
        review_entity_type_id: number;
    }[];
    parent_ids?: number[];
    review_definition_ids?: number[];
    review_definitions?: ReviewDefinition[];
    gated_reviews?: GatedReview[] | null;
}

export interface ReviewAttribute {
    id: number;
    name: string;
    description: string;
    value: string;
    review_entity_id: number;
}

// We'll maintain our response type as ReviewEntity[] to match component expectations
export const getReviewEntities = async (): Promise<AxiosResponse<ReviewEntity[]>> => {
    const endpoint = '/review_entities';
    return fetchWithProtobufSupport<ReviewEntity>(endpoint);
};

export const getReviewEntity = async (id: number): Promise<AxiosResponse<ReviewEntity>> => {
    const endpoint = '/review_entity';
    const params = { id };
    
    // Use regular API first since we need a single entity, not an array
    try {
        // Try regular JSON API for single entity
        return await api.get<ReviewEntity>(endpoint, { params });
    } catch (error) {
        console.error('Error fetching entity with JSON API:', error);
        
        try {
            // Try with protobuf API but handle array response specially
            if (!shouldUseProtobuf(endpoint)) {
                throw new Error('Endpoint does not support protobuf');
            }
            
            const response = await protobufApi.get(endpoint, {
                params,
                responseType: 'arraybuffer'
            });
            
            // If we got a protobuf response
            if (response.headers['content-type'] === 'application/x-protobuf') {
                try {
                    const buffer = new Uint8Array(response.data);
                    const decoded = ReviewEntityList.decode(buffer);
                    
                    if (decoded.entities && decoded.entities.length > 0) {
                        // Get first entity and convert it
                        const entity = adaptProtoEntityToFrontend(decoded.entities[0]);
                        return { ...response, data: entity };
                    }
                } catch (decodeError) {
                    console.error('Failed to decode protobuf for single entity:', decodeError);
                }
            } else if (response.headers['content-type']?.includes('application/json')) {
                // Handle JSON response from protobuf API
                try {
                    const textDecoder = new TextDecoder();
                    const jsonText = textDecoder.decode(response.data);
                    const jsonData = JSON.parse(jsonText);
                    
                    if (Array.isArray(jsonData) && jsonData.length > 0) {
                        return { ...response, data: jsonData[0] };
                    } else if (!Array.isArray(jsonData)) {
                        return { ...response, data: jsonData };
                    }
                } catch (jsonError) {
                    console.error('Error parsing JSON for single entity:', jsonError);
                }
            }
        } catch (protoError) {
            console.error('Error with protobuf fallback:', protoError);
        }
        
        // Final fallback attempt with standard API
        return api.get<ReviewEntity>(endpoint, { params });
    }
};

export const updateReviewEntity = (data: {
    id: number;
    name?: string;
    description?: string;
    org_entity_code?: string;
    review_entity_type_id?: number;
    reviewers?: number[];
    reviewees?: number[];
    data_entry_supports?: number[];
    owner?: number;
    status?: string;
    valid_from?: string;
    valid_till?: string;
    parent_ids?: number[];
    review_definition_ids?: number[];
    gated_reviews?: GatedReview[] | null;
}): Promise<AxiosResponse<ReviewEntity>> =>
    api.put(`/update_review_entity`, data);

export const createReviewEntity = (data: {
    name: string;
    description: string;
    org_entity_code?: string;
    review_entity_type_id: number;
    reviewers: number[];
    reviewees: number[];
    data_entry_supports?: number[];
    owner?: number;
    parent_ids?: number[];
    review_definition_ids: number[];
    gated_reviews?: GatedReview[] | null;
}) => 
    api.post('/review_entity', data);

export const deleteReviewEntity = (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/delete_review_entity?id=${id}`);

export const getReviewFrequencies = (): Promise<AxiosResponse<ReviewFrequency[]>> =>
    api.get('/review_frequencies');
    
export const getReviewDimensions = (): Promise<AxiosResponse<ReviewDimension[]>> =>
    api.get('/review_dimensions');

export const deleteReviewFrequency = (name: string): Promise<AxiosResponse<any>> =>
    api.delete(`/review_frequency/${name}`);

export const createReviewDimension = (dimension: { name: string; description: string }): Promise<AxiosResponse<any>> =>
    api.post('/review_dimensions', dimension);

export const updateReviewDimension = (dimension: { name: string; description: string }): Promise<AxiosResponse<any>> =>
    api.put('/review_dimensions', dimension);

export const deleteReviewDimension = (name: string): Promise<AxiosResponse<any>> =>
    api.delete(`/review_dimensions/${name}`);

/** Flexible type metadata for attribute keys (categories, bounds, text validations, etc.). */
export interface AttributeValueJson {
    categories?: string[];
    min?: number;
    max?: number;
    precision?: number;
    min_length?: number;
    max_length?: number;
    pattern?: string;
    [key: string]: unknown;
}

export interface AttributeKey {
    id: string;
    name: string;
    display_label: string;
    description: string;
    data_type?: string;
    value_json?: AttributeValueJson;
}

export interface ReviewEntityAttributeType {
    name: string;
    display_label: string;
    description?: string;
    data_type?: string;
    value_json?: AttributeValueJson;
}

export const getReviewEntityAttributeTypes = (reviewEntityTypeId: number): Promise<AxiosResponse<ReviewEntityAttributeType[]>> =>
    api.get(`/review_entity_attribute_keys_by_review_entity_type_id?review_entity_type_id=${reviewEntityTypeId}`);

export const getReviewEntityAttributeKeys = (reviewEntityTypeId?: number): Promise<AxiosResponse<AttributeKey[]>> =>
    reviewEntityTypeId
        ? api.get(`/review_entity_attribute_keys_by_review_entity_type_id?review_entity_type_id=${reviewEntityTypeId}`)
        : api.get(`/review_entity_attribute_keys`);

// Updated to use the new endpoint format that accepts a list of attributes

export const createReviewEntityAttribute = (entityId: number, data: {
    name: string;
    description: string;
    value: string;
}) => api.put('/update_review_entity_attributes', { 
    id: entityId, 
    attributes: [data] 
});

export const updateReviewEntityAttribute = (
    entityId: number,
    attributes: Array<{ name: string; description: string; value: string }>
): Promise<AxiosResponse<any>> =>
    api.put(`/update_review_entity_attributes`, { id: entityId, attributes: attributes });

// For deleting an attribute, we use the delete_review_entity_attribute endpoint
export const deleteReviewEntityAttribute = (entityId: number, attributeName: string): Promise<AxiosResponse<any>> =>
    api.delete(`/delete_review_entity_attribute?id=${entityId}&attribute_name=${encodeURIComponent(attributeName)}`);

// Questionnaire Response APIs
export interface QuestionnaireResponse {
    id: number;
    review_name: string;
    review_definition?: {
        id: number;
        name: string;
        display_label: string;
        description: string;
    };
    review_definition_id?: number;
    review_frequency: string;
    review_header: string;
    reviewer_id: number;
    reviewer_name: string;
    reviewee_id: number;
    reviewee_name: string;
    review_date: string;
    review_period_start: string;
    review_period_end: string;
    review_period_display: string;
    review_start_timestamp: string;
    review_end_timestamp: string | null;
    review_entity_id: number;
    review_entity_name: string;
    review_entity_type_id?: number;
    review_entity_type_name?: string | {
        id: number;
        name: string;
        display_label: string;
        category: string;
        description: string;
        associated_review_dimensions?: number[];
        associated_review_dimension_names?: string[];
    };
    review_status: string;
    review_comments: string | null;
    created_by: number;
    created_at: string;
    updated_by: number;
    updated_at: string;
    created_by_name: string;
    updated_by_name: string;
    is_submission_overdue?: boolean;
    submission_due_by?: string | null;
    days_past_submission_due?: number | null;
    is_closure_overdue?: boolean;
    submitted_on?: string | null;
    closure_due_by?: string | null;
    days_past_closure_due?: number | null;
    is_overdue?: boolean;
    overdue_stage?: 'SUBMISSION' | 'CLOSURE' | null;
}

export interface QuestionnaireResponsePayload {
    review_name: string;
    review_definition_id: number;
    review_header: string;
    review_comments?: string;
    reviewer_id: number;
    reviewee_id: number;
    review_date: string;
    review_entity_id: number;
    /** Optional "HH:mm" (24h). When set, backend sets review_start_timestamp on create. */
    review_start_time?: string | null;
}

export const getReviewEntityByReviewee = async (revieweeId: number): Promise<AxiosResponse<ReviewEntity[]>> => {
    const endpoint = `/review_entity_by_reviewee/${revieweeId}`;
    
    // Get API response
    const response = await fetchWithProtobufSupport<ReviewEntity>(endpoint);
    
    // Convert API response to component type explicitly
    const componentData = adaptEntityList(response.data);
    return { ...response, data: componentData };
};

export const getQuestionnaireResponses = () =>
    api.get<QuestionnaireResponse[]>('/review_questionnaire_responses');

export const getQuestionnaireResponsesByReviewEntity = (reviewEntityId: number) =>
    api.get<QuestionnaireResponse[]>(`/review_questionnaire_responses/by_review_entity/${reviewEntityId}`);
    
/**
 * Get a single questionnaire response by ID
 * @param id The review questionnaire response ID
 * @returns Promise with the questionnaire response details
 */
export const getQuestionnaireResponseById = (id: number) =>
    api.get<QuestionnaireResponse>(`/review_questionnaire_responses/${id}`);

export const createQuestionnaireResponse = (data: QuestionnaireResponsePayload) =>
    api.post('/review_questionnaire_responses', data);

/**
 * Get active questionnaire responses for reviewee (DRAFT, IN_PROGRESS, CORRECTION_PENDING)
 * @returns Promise with array of active questionnaire responses
 */
export const getRevieweeActiveQuestionnaireResponses = () =>
    api.get<QuestionnaireResponse[]>('/reviewee_active_questionnaire_responses');

/**
 * Get closed questionnaire responses for reviewee (SUBMITTED, CLOSED, CANCELLED)
 * @returns Promise with array of closed questionnaire responses
 */
export const getRevieweeClosedQuestionnaireResponses = () =>
    api.get<QuestionnaireResponse[]>('/reviewee_closed_questionnaire_responses');

/**
 * Get active questionnaire responses for reviewer (SUBMITTED)
 * @returns Promise with array of active questionnaire responses
 */
export const getReviewerActiveQuestionnaireResponses = () =>
    api.get<QuestionnaireResponse[]>('/reviewer_active_questionnaire_responses');

/**
 * Get closed questionnaire responses for reviewer (CLOSED, CANCELLED)
 * @returns Promise with array of closed questionnaire responses
 */
export const getReviewerClosedQuestionnaireResponses = () =>
    api.get<QuestionnaireResponse[]>('/reviewer_closed_questionnaire_responses');

/** User-scoped review pipeline for dashboard (draft, submitted, submission/closure overdue). */
export interface ReviewQuestionnaireWorkflowRecord {
    id: number;
    review_name: string;
    review_definition_id: number;
    review_header: string;
    reviewer_id: number;
    reviewer_name: string;
    reviewee_id: number;
    reviewee_name: string;
    review_date: string | null;
    review_start_timestamp: string | null;
    review_end_timestamp: string | null;
    review_entity_id: number;
    review_entity_name: string;
    review_entity_type_name: string;
    review_status: string;
    review_comments: string | null;
    predecessor_review_questionnaire_response_id: number | null;
    review_definition?: unknown;
    submission_due_by?: string | null;
    days_past_submission_due?: number;
    submitted_on?: string | null;
    closure_due_by?: string | null;
    days_past_closure_due?: number;
}

export interface ReviewQuestionnaireWorkflowResponse {
    draft: ReviewQuestionnaireWorkflowRecord[];
    submitted: ReviewQuestionnaireWorkflowRecord[];
    submission_overdue: ReviewQuestionnaireWorkflowRecord[];
    closure_overdue: ReviewQuestionnaireWorkflowRecord[];
}

export const getReviewQuestionnaireWorkflow = () =>
    api.get<ReviewQuestionnaireWorkflowResponse>('/review_questionnaire_responses/workflow');

// Review Questions and Answers APIs
export const getReviewQuestionsByResponseId = async (reviewDefinitionId: number, reviewEntityTypeId: number): Promise<AxiosResponse<ReviewQuestion[]>> => {
    const endpoint = `/review_questions/review_definition/${reviewDefinitionId}/review_entity_type/${reviewEntityTypeId}`;
    return fetchWithProtobufSupport<ReviewQuestion>(endpoint);
};

export const getReviewQuestionAnswers = async (responseId: number): Promise<AxiosResponse<ReviewQuestionAnswer[]>> => {
    const endpoint = `/review_question_answer`;
    const params = { review_questionnaire_response_id: responseId };
    // This endpoint might not be protobuf-enabled, but our helper will handle that gracefully
    return fetchWithProtobufSupport<ReviewQuestionAnswer>(endpoint, params);
};

export const getReviewQuestionAnswersWithDimension = async (responseId: number): Promise<AxiosResponse<any>> => {
    const endpoint = `/review_question_answer_with_dimension/${responseId}`;
    // This is a newer endpoint that might not have protobuf support yet
    return api.get(endpoint);
};

export const submitReviewAnswer = (data: {
    review_question_id: number;
    review_questionnaire_response_id: number;
    answer_value: string[];
    submitted_by: number;
}) => api.post('/review_question_answer', data);

export const getReviewQuestionsByTypeAndFrequency = async (reviewEntityType: string, reviewFrequency: string): Promise<AxiosResponse<ComponentReviewQuestion[]>> => {
    const endpoint = `/review_questions/review_entity_type/${encodeURIComponent(reviewEntityType)}/review_frequency/${encodeURIComponent(reviewFrequency)}`;
    // Use type casting to avoid TypeScript errors
    const response = await api.get(endpoint);
    
    // Adapt each question to the component's expected type with all required fields
    const adaptedQuestions = (response.data || []).map((question: ReviewQuestion) => {
        return {
            id: question.id,
            review_question_text: question.review_question_text || '',
            review_entity_type: question.review_entity_type || '',
            description: question.description || '',
            review_dimension: question.review_dimension || '',
            review_frequency: question.review_frequency || '',
            answer_type: question.answer_type || '',
            answer_options: Array.isArray(question.answer_options) ? question.answer_options : [],
            review_question_type: question.review_question_type || '',
            action_required: question.action_required || '',
            default_value: question.default_value || '',
            ideal_value: question.ideal_value || '',
            created_by: '',
            created_at: '',
            updated_at: '',
            updated_by: ''
        } as ComponentReviewQuestion;
    });
    
    // Return response with properly typed data
    return { ...response, data: adaptedQuestions };
};

// RAID Item APIs - New Structure
// Common enums for all RAID items
export enum SeverityEnum {
    HIGH = "High",
    MEDIUM = "Medium",
    LOW = "Low",
}

export enum ProbabilityEnum {
    HIGH = "High",
    MEDIUM = "Medium",
    LOW = "Low",
}

export enum PriorityEnum {
    HIGH = "High",
    MEDIUM = "Medium",
    LOW = "Low",
}

export enum ImpactAreaEnum {
    SCHEDULE = "Schedule",
    COST = "Cost",
    EFFORT = "Effort",
    QUALITY = "Quality",
}

export enum TypeEnum {
    INTERNAL = "Internal",
    CUSTOMER = "Customer",
    THIRD_PARTY = "3rd Party",
}

// Risk specific types
export enum RiskStatusEnum {
    OPEN = "Open",
    OVERDUE = "Overdue",
    MITIGATED = "Mitigated",
    ACCEPTED = "Accepted",
}

export interface RiskDetails {
    description: string;
    severity?: SeverityEnum;
    probability?: ProbabilityEnum;
    impact_area?: ImpactAreaEnum;
    date_of_occurrence?: string;
    type?: TypeEnum;
    mitigation_plan?: string;
    person_responsible?: string;
    proposed_date?: string;
    actual_date?: string;
    contingency_plan?: string;
    contingency_person_responsible?: string;
    contingency_proposed_date?: string;
    contingency_actual_date?: string;
    status?: RiskStatusEnum;
    source_dimension?: string;
}

// Assumption specific types
export enum AssumptionStatusEnum {
    OPEN = "Open",
    VALIDATED = "Validated",
    INVALIDATED = "Invalidated",
}

export interface AssumptionDetails {
    description: string;
    rationale?: string;
    impact?: string;
    mitigation_plan?: string;
    contingency_plan?: string;
    status?: AssumptionStatusEnum;
    person_responsible?: string;
    proposed_date?: string;
    actual_date?: string;
    contingency_person_responsible?: string;
    contingency_proposed_date?: string;
    contingency_actual_date?: string;
    resolution_description?: string;
}

// Issue specific types
export enum IssueStatusEnum {
    OPEN = "Open",
    CANCELLED = "Cancelled",
    RESOLVED = "Resolved",
}

export interface IssueDetails {
    description: string;
    severity?: SeverityEnum;
    impact_area?: string;
    date_of_occurrence?: string;
    type?: TypeEnum;
    resolution_plan?: string;
    person_responsible?: string;
    proposed_date?: string;
    actual_resolution_date?: string;
    status?: IssueStatusEnum;
    source_dimension?: string;
    impact_areas?: string[];
}

// Dependency specific types
export enum DependencyStatusEnum {
    OPEN = "Open",
    OVERDUE = "Overdue",
    FULFILLED = "Fulfilled",
    CANCELLED = "Cancelled",
}

export interface DependencyDetails {
    type?: TypeEnum;
    expected_fulfuillment_date?: string;
    description: string;
    status?: DependencyStatusEnum;
    impact_area?: ImpactAreaEnum;
    person_responsible?: string;
    priority?: PriorityEnum;
    severity?: SeverityEnum;
    actual_fulfillment_date?: string;
    source_dimension?: string;
}

// Action specific types
export enum ActionStatusEnum {
    OPEN = "Open",
    CLOSED = "Closed",
    ABANDONED = "Abandoned",
}

export interface ActionDetails {
    description: string;
    priority?: PriorityEnum;
    planned_competion_date?: string;  // Keeping the same spelling as the backend to ensure compatibility
    actual_completion_date?: string;
    person_responsible?: string;
    source_dimension?: string;
    status?: ActionStatusEnum;
}

// Opportunity specific types
export enum OpportunityCategoryEnum {
    COST = "Cost",
    REVENUE = "Revenue",
    SCHEDULE = "Schedule",
    QUALITY = "Quality",
    PRODUCTIVITY = "Productivity",
    CUSTOMER = "Customer",
    INNOVATION = "Innovation",
    RESOURCE = "Resource",
    DELIVERY = "Delivery",
    COMMERCIAL = "Commercial",
}

export enum OpportunityConfidenceEnum {
    HIGH = "High",
    MEDIUM = "Medium",
    LOW = "Low",
}

export enum OpportunityStatusEnum {
    IDENTIFIED = "Identified",
    UNDER_EVALUATION = "Under Evaluation",
    APPROVED = "Approved",
    IN_PROGRESS = "In Progress",
    REALIZED = "Realized",
    CLOSED = "Closed",
}

export interface OpportunityDetails {
    description: string;
    category?: string[];
    expected_benefit?: string;
    business_impact?: string;
    estimated_value?: number;
    confidence_level?: OpportunityConfidenceEnum;
    investment_required?: string;
    person_responsible?: string;
    expected_closure_date?: string;
    actual_closure_date?: string;
    status?: OpportunityStatusEnum;
    execution_plan?: string;
    source_dimension?: string;
}

// NCR specific types
export enum NcrCategoryEnum {
    PROCESS = "Process",
    QUALITY = "Quality",
    SECURITY = "Security",
    COMPLIANCE = "Compliance",
    TESTING = "Testing",
    DOCUMENTATION = "Documentation",
}

export enum NcrSubCategoryEnum {
    CHANGE_MANAGEMENT = "Change Management",
    PEER_REVIEW = "Peer Review",
    TEST_COVERAGE = "Test Coverage",
}

export enum NcrSeverityEnum {
    CRITICAL = "Critical",
    MAJOR = "Major",
    MINOR = "Minor",
    OBSERVATION = "Observation",
}

export enum NcrImpactAreaEnum {
    SCHEDULE = "Schedule",
    COST = "Cost",
    QUALITY = "Quality",
    SECURITY = "Security",
    COMPLIANCE = "Compliance",
    CUSTOMER_SATISFACTION = "Customer Satisfaction",
}

export enum NcrDetectionMethodEnum {
    AUDIT = "Audit",
    REVIEW = "Review",
    TESTING = "Testing",
    MONITORING = "Monitoring",
    CUSTOMER_FEEDBACK = "Customer Feedback",
}

export enum NcrRootCauseCategoryEnum {
    PROCESS_GAP = "Process Gap",
    TOOL_LIMITATION = "Tool Limitation",
    PLANNING_ERROR = "Planning Error",
    HUMAN_ERROR = "Human Error",
    COMMUNICATION_GAP = "Communication Gap",
}

export enum NcrStatusEnum {
    OPEN = "Open",
    UNDER_INVESTIGATION = "Under Investigation",
    CORRECTIVE_ACTION = "Corrective Action",
    VERIFICATION = "Verification",
    CLOSED = "Closed",
}

export enum NcrVerificationMethodEnum {
    INTERNAL_AUDIT = "Internal Audit",
    QA_VERIFICATION = "QA Verification",
    CUSTOMER_ACCEPTANCE = "Customer Acceptance",
}

export interface NcrDetails {
    description: string;
    category?: NcrCategoryEnum;
    sub_category?: NcrSubCategoryEnum;
    requirement_violated?: string;
    source_of_requirement?: string;
    severity?: NcrSeverityEnum;
    business_impact?: string;
    impact_area?: NcrImpactAreaEnum;
    detected_on?: string;
    detected_by?: string;
    detection_method?: NcrDetectionMethodEnum;
    occurrence_date?: string;
    root_cause?: string;
    root_cause_category?: NcrRootCauseCategoryEnum;
    immediate_correction?: string;
    corrective_action?: string;
    preventive_action?: string;
    person_responsible?: string;
    target_closure_date?: string;
    actual_closure_date?: string;
    status?: NcrStatusEnum;
    verification_method?: NcrVerificationMethodEnum;
    verified_by?: string;
    evidence?: string;
    related_risks?: string;
    related_issues?: string;
    related_change_requests?: string;
    repeat_occurrence?: boolean;
    previous_ncr_reference?: string;
    lessons_learned?: string;
    management_comments?: string;
    source_dimension?: string;
}

// Observation specific types
export enum ObservationStatusEnum {
    OPEN = "Open",
    CLOSED = "Closed",
}

export interface ObservationDetails {
    observation: string;
    captured_by?: string;
    captured_on?: string;
    status?: ObservationStatusEnum;
    source_dimension?: string;
}

// Common RAID item types
export enum RaidCategoryEnum {
    RISK = "RISK",
    ASSUMPTION = "ASSUMPTION",
    ISSUE = "ISSUE",
    DEPENDENCY = "DEPENDENCY",
    ACTION = "ACTION",
    OPPORTUNITY = "OPPORTUNITY",
    NCR = "NCR",
    OBSERVATION = "OBSERVATION",
}

// Request and response interfaces
export interface DetailResponse {
    detail_id: number;
    status: string;
    content: any;
    created_by: number;
    created_at: string;
}

export interface RaidItemResponse {
    raid_id: number;
    entity_type: string;
    entity_id: number;
    review_entity_dimension?: string;
    title: string;
    raid_category: RaidCategoryEnum;
    questionnaire_response_id?: number;
    review_name?: string;
    review_date?: string;
    review_status?: string;
    initiated_by: number;
    is_system_generated?: boolean;
    partial_raid?: boolean;
    details: DetailResponse[];
    created_by: number;
    created_at: string;
    updated_by: number;
    updated_at: string;
}

export interface RaidItemsRequest {
    entity_type: string;
    entity_id: number;
    review_dimension?: string;
}

export interface RaidItemCreate {
    entity_id: number;
    entity_type?: string;
    review_dimension?: string;
    title: string;
    raid_category: RaidCategoryEnum;
    partial_raid?: boolean;
    details: RiskDetails | AssumptionDetails | IssueDetails | DependencyDetails | ActionDetails | OpportunityDetails | NcrDetails | ObservationDetails;
}

// Define RaidItem interface for use throughout the app
export interface RaidItem {
    raid_id: number;
    entity_type: string;
    entity_id: number;
    review_entity_dimension?: string;
    title: string;
    raid_category: RaidCategoryEnum;
    questionnaire_response_id?: number;
    review_name?: string;
    review_date?: string;
    review_status?: string;
    initiated_by: number;
    is_system_generated?: boolean;
    partial_raid?: boolean;
    details: DetailResponse[];
    created_by: number;
    created_at: string;
    updated_by: number;
    updated_at: string;
}

// Interface for separated RAID items by questionnaire
export interface SeparatedRaidItems {
    current: RaidItem[];
    others: RaidItem[];
}

// API endpoints
export const getRaidItems = (request: RaidItemsRequest | number) => {
    // If request is a number, convert it to a RaidItemsRequest object
    if (typeof request === 'number') {
        return api.post('/raid_items/search', {
            entity_type: 'review_question_answer',
            entity_id: request
        });
    }
    return api.post('/raid_items/search', request);
};

// New function to get RAID items across all questionnaires for a specific question answer
export const getRaidItemsAllQuestionnaires = (reviewQuestionAnswerId: number) => {
    return api.get(`/raid_items/question_answer/${reviewQuestionAnswerId}/all_questionnaires`);
};

// New function to get all RAID items for a review entity (both entity level and question answer level)
export const getRaidItemsByReviewEntity = (
    reviewEntityId: number,
    options?: { detailsLimit?: number }
) => {
    const params: Record<string, string> = {};
    if (options?.detailsLimit != null) {
        params.details_limit = String(options.detailsLimit);
    }
    return api.get(`/raid_items/review_entity/${reviewEntityId}`, {
        params: Object.keys(params).length ? params : undefined,
    });
};

export const getRaidItemById = (id: number) =>
    api.get(`/raid_items/${id}`);

export const createRaidItem = (data: RaidItemCreate) =>
    api.post('/raid_items', data);
    
export const updateRaidItem = (id: number, data: RaidItemCreate) =>
    api.put(`/raid_items/${id}`, data);

export interface UpdateReviewQuestionPayload {
    question_id: number;
    review_question_text?: string;
    description?: string;
    review_dimension_id?: number;
    review_definition_id?: number;
    review_entity_type_id?: number;
    answer_type?: string;
    answer_options?: string[];
    review_question_type?: string;
    action_required?: string;
    default_value?: string;
    ideal_value?: string;
}

export const updateReviewQuestion = (payload: UpdateReviewQuestionPayload) =>
    api.put('/update_review_question', payload);

export const deleteReviewQuestion = (questionId: number) =>
    api.delete(`/delete_review_question/${questionId}`);

// Metrics APIs

/**
 * Create a WebSocket connection for metrics computation progress updates
 * with built-in DOM updating to ensure real-time UI feedback
 * @param clientId Unique client identifier for this connection
 * @returns WebSocket instance
 */
export const createMetricsWebSocketConnection = (clientId: string): WebSocket => {
    // Use the same host/port as the API server but with ws/wss protocol
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    
    // Parse the server URL
    const serverUrl = new URL(CONFIG.SERVER_URL);
    const host = serverUrl.host; // Gets hostname:port
    
    // Construct the WebSocket URL
    const wsUrl = `${protocol}//${host}/ws/metrics-compute/${clientId}`;
    
    const timestamp = new Date().toLocaleTimeString();
    console.log(`[${timestamp}] Creating WebSocket connection to: ${wsUrl}`);
    
    // Create WebSocket instance
    const ws = new WebSocket(wsUrl);
    
    // Add automatic direct DOM update for real-time UI feedback
    ws.addEventListener('message', (event) => {
        const msgTime = new Date().toLocaleTimeString();
        
        try {
            // Skip if this is a heartbeat response
            if (event.data === 'pong') return;
            
            const data = JSON.parse(event.data);
            const timestamp = new Date().toLocaleTimeString();
            console.log(`[${timestamp}] Real-time update received: ${data.status} - ${data.progress}%`);
            
            // Direct DOM update for immediate feedback
            const progressBar = document.querySelector('#metrics-progress-bar .MuiLinearProgress-bar');
            if (progressBar instanceof HTMLElement) {
                progressBar.style.transform = `translateX(${data.progress - 100}%)`;
            }
            
            const progressText = document.getElementById('metrics-progress-text');
            if (progressText) {
                progressText.textContent = `${Math.round(data.progress)}%`;
            }
            
            const messageElement = document.getElementById('metrics-message');
            if (messageElement) {
                messageElement.textContent = data.message;
            }
            
            const dimensionElement = document.getElementById('metrics-dimension');
            if (dimensionElement && data.current_dimension) {
                dimensionElement.textContent = `Processing: ${data.current_dimension}`;
                dimensionElement.style.display = 'block';
            } else if (dimensionElement) {
                dimensionElement.style.display = 'none';
            }
        } catch (err) {
            console.error(`[${msgTime}] Error in WebSocket DOM updater:`, err);
        }
    });
    
    return ws;
};

/**
 * Trigger metrics computation with progress tracking via polling.
 * @returns Promise with the computation results
 */
export const triggerMetricsCompute = () =>
    api.post(`/trigger-metrics-compute/`);

/**
 * Get the current system environment (local, dev, qa, prod, etc.)
 * @returns Promise with the system environment information
 */
export const getSystemEnvironment = () => api.get('/system-env');

/**
 * Get the setting for showing other users' review entities
 * @returns Promise with the show_other_users_review_entities config value
 */
export const getShowOtherUsersReviewEntities = async () => {
    console.log('DEBUG: Starting getShowOtherUsersReviewEntities call');
    const result = await api.get('/show-other-users-review-entities');
    console.log('DEBUG: Show other users review entities response:', result);
    return result;
};

/**
 * Get review entities for which the current user is a reviewer or reviewee
 * @returns Promise with the user's review entities
 */
export const getMyReviewEntities = async (): Promise<AxiosResponse<ReviewEntity[]>> => {
    const endpoint = '/get_my_review_entities';
    console.log('DEBUG: Starting getMyReviewEntities call to:', endpoint);
    
    try {
        // First try with fetchWithProtobufSupport
        console.log('DEBUG: Trying with fetchWithProtobufSupport');
        const response = await fetchWithProtobufSupport<ReviewEntity>(endpoint);
        console.log('DEBUG: Raw API response from getMyReviewEntities:', response);
        
        // Convert API response to component type explicitly
        const componentData = adaptEntityList(response.data);
        console.log('DEBUG: Transformed data from getMyReviewEntities:', componentData);
        return { ...response, data: componentData };
    } catch (error) {
        console.error('DEBUG: Error in getMyReviewEntities. Trying with direct API call...', error);
        
        // Try with direct API call as fallback
        try {
            const directResponse = await api.get(endpoint);
            console.log('DEBUG: Direct API response:', directResponse);
            const componentData = adaptEntityList(directResponse.data);
            return { ...directResponse, data: componentData };
        } catch (directError) {
            console.error('DEBUG: Direct API call also failed:', directError);
            throw directError;
        }
    }
};

/**
 * Get review entities where the user is assigned as a reviewer
 * @param reviewerId The ID of the reviewer
 * @returns Promise with the review entities
 */
export const getReviewEntitiesByReviewer = async (reviewerId: number): Promise<AxiosResponse<ReviewEntity[]>> => {
    const endpoint = `/review_entity_by_reviewer/${reviewerId}`;
    console.log('DEBUG: Starting getReviewEntitiesByReviewer call to:', endpoint);
    
    try {
        // First try with fetchWithProtobufSupport
        console.log('DEBUG: Trying with fetchWithProtobufSupport');
        const response = await fetchWithProtobufSupport<ReviewEntity>(endpoint);
        console.log('DEBUG: Raw API response from getReviewEntitiesByReviewer:', response);
        
        // Convert API response to component type explicitly
        const componentData = adaptEntityList(response.data);
        console.log('DEBUG: Transformed data from getReviewEntitiesByReviewer:', componentData);
        return { ...response, data: componentData };
    } catch (error) {
        console.error('DEBUG: Error in getReviewEntitiesByReviewer. Trying with direct API call...', error);
        
        // Try with direct API call as fallback
        try {
            const directResponse = await api.get(endpoint);
            console.log('DEBUG: Direct API response:', directResponse);
            const componentData = adaptEntityList(directResponse.data);
            return { ...directResponse, data: componentData };
        } catch (directError) {
            console.error('DEBUG: Direct API call also failed:', directError);
            throw directError;
        }
    }
}

/**
 * Update the comments for a review questionnaire response
 * @param id The review questionnaire response ID
 * @param comments The comments to add
 * @returns Promise with the updated review questionnaire response
 */
export const updateReviewComments = (id: number, comments: string) => api.put('/review_questionnaire_responses/comments', {
  id,
  param_name: 'review_comments',
  param_value: comments
});

/**
 * Submit a review questionnaire response (final submission) 
 * @param id The review questionnaire response ID
 * @returns Promise with the updated review questionnaire response
 */
export const submitReview = (id: number) => api.put(`/review_questionnaire_responses/submit?id=${id}`);

/**
 * @deprecated Use submitReview instead - this function uses the incorrect endpoint
 */
export const closeReview = submitReview;

/**
 * Get review summary data for a questionnaire response
 * @param responseId The review questionnaire response ID
 * @returns Promise with the review summary data
 */
export const getReviewSummary = async (responseId: number): Promise<AxiosResponse<any>> => {
    const endpoint = `/reviewsummary?review_questionnaire_response_id=${responseId}`;
    console.log('DEBUG: Starting getReviewSummary call to:', endpoint);
    
    try {
        // First try with direct API call since protobuf may not be set up for this endpoint yet
        const response = await api.get(endpoint);
        console.log('DEBUG: Review summary API response:', response);
        return response;
    } catch (error) {
        console.error('DEBUG: Error fetching review summary:', error);
        throw error;
    }
};

/**
 * Get full textual review summary for a questionnaire response
 * @param responseId The review questionnaire response ID
 * @returns Promise with the full textual review summary
 */
export const getFullReviewSummary = async (responseId: number): Promise<AxiosResponse<any>> => {
    // Using the same format as getReviewSummary for consistency
    const endpoint = `/reviewsummary/get_full_review_summary?review_questionnaire_response_id=${responseId}`;
    console.log('DEBUG: Starting getFullReviewSummary call to:', endpoint);
    
    try {
        const response = await api.get(endpoint);
        console.log('DEBUG: Full review summary API response:', response);
        return response;
    } catch (error) {
        console.error('DEBUG: Error fetching full review summary:', error);
        throw error;
    }
};

/**
 * Download PDF report: saved LLM textual summary, RAID breakdown, and metric trends
 * (same trending service as the Metrics Trending report).
 */
export const downloadReviewSummaryPdfReport = async (responseId: number): Promise<void> => {
    const endpoint = `/reviewsummary/pdf_report`;
    try {
        const response = await api.get(endpoint, {
            params: { review_questionnaire_response_id: responseId },
            responseType: 'blob',
        });
        const blob =
            response.data instanceof Blob
                ? response.data
                : new Blob([response.data], { type: 'application/pdf' });
        if (!blob.size) {
            throw new Error('Empty PDF response.');
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `review_summary_${responseId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
        if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
            const blob = error.response.data;
            const text = await blob.text();
            let msg: string | undefined;
            try {
                const j = JSON.parse(text) as { detail?: unknown };
                const d = j.detail;
                msg =
                    typeof d === 'string'
                        ? d
                        : Array.isArray(d)
                          ? JSON.stringify(d)
                          : typeof d === 'object' && d !== null && 'msg' in d
                            ? String((d as { msg: unknown }).msg)
                            : undefined;
            } catch {
                /* not JSON */
            }
            throw new Error(
                msg || text?.slice(0, 500) || `Download failed (${error.response.status})`
            );
        }
        throw error;
    }
};

/**
 * Add reviewer comments to a review summary
 * @param responseId The review questionnaire response ID
 * @param comments The reviewer comments
 * @param reviewerId The ID of the reviewer
 * @returns Promise with the updated review summary
 */
export const addReviewerComments = async (responseId: number, comments: string, reviewerId: number): Promise<AxiosResponse<any>> => {
    const endpoint = `/reviewsummary/add_reviewer_comments`;
    console.log('DEBUG: Adding reviewer comments for response ID:', responseId, 'reviewer ID:', reviewerId);
    
    try {
        const response = await api.post(endpoint, {
            review_questionnaire_response_id: responseId,
            reviewer_comments: comments,
            reviewer_id: reviewerId
        });
        console.log('DEBUG: Add reviewer comments response:', response);
        return response;
    } catch (error) {
        console.error('DEBUG: Error adding reviewer comments:', error);
        throw error;
    }
};

/**
 * Finalize a review (final closure)
 * @param responseId The review questionnaire response ID
 * @returns Promise with the closure response
 */
export const finalizeReview = async (responseId: number): Promise<AxiosResponse<any>> => {
    const endpoint = `/review_questionnaire_responses/close?id=${responseId}`;
    console.log('DEBUG: Finalizing review for response ID:', responseId);
    
    try {
        const response = await api.put(endpoint);
        console.log('DEBUG: Finalize review response:', response);
        return response;
    } catch (error) {
        console.error('DEBUG: Error finalizing review:', error);
        throw error;
    }
};

/**
 * Get the status of a review questionnaire response
 * @param responseId The review questionnaire response ID
 * @returns Promise with the review status
 */
export const getReviewStatus = async (responseId: number): Promise<AxiosResponse<any>> => {
    const endpoint = `/review_questionnaire_responses/status?id=${responseId}`;
    console.log('DEBUG: Getting review status for response ID:', responseId);
    
    try {
        const response = await api.get(endpoint);
        console.log('DEBUG: Review status response:', response);
        return response;
    } catch (error) {
        console.error('DEBUG: Error getting review status:', error);
        throw error;
    }
};

/**
 * Get the list of available LLM providers and models
 * @returns Promise with the LLM provider and model list
 */
export const getLLMProviderAndModelList = async (): Promise<AxiosResponse<any>> => {
    const endpoint = `/llm-provider-and-model-list`;
    console.log('DEBUG: Getting LLM provider and model list');
    
    try {
        const response = await api.get(endpoint);
        console.log('DEBUG: LLM provider and model list response:', response);
        return response;
    } catch (error) {
        console.error('DEBUG: Error getting LLM provider and model list:', error);
        throw error;
    }
};

/**
 * Generate a review summary using LLM
 * @param reviewSummaryRequest The request object containing response ID, provider, model, and save flag
 * @returns Promise with the generated review summary
 */
export const generateReviewSummary = async (reviewSummaryRequest: {
    review_questionnaire_response_id: number;
    llm_provider: string;
    llm_model: string;
    save_summary: boolean;
    override_existing_summary?: boolean;
}): Promise<AxiosResponse<any>> => {
    const endpoint = `/reviewsummary/generate`;
    console.log('DEBUG: Generating review summary with request:', reviewSummaryRequest);
    
    try {
        const response = await api.post(endpoint, reviewSummaryRequest);
        console.log('DEBUG: Generate review summary response:', response);
        return response;
    } catch (error) {
        console.error('DEBUG: Error generating review summary:', error);
        throw error;
    }
};

// Metrics Threshold APIs
export interface MetricsThreshold {
    review_entity_id: number;
    org_threshold_id: number;
    threshold_name: string;
    threshold_description: string;
    threshold_dimension: string;
    org_threshold_lower_bound: number;
    org_threshold_upper_bound: number;
    overridable: boolean;
    overriden_lower_bound?: number | null;
    overriden_upper_bound?: number | null;
    override_reason?: string | null;
    org_is_critical?: boolean | null;
    org_is_dashboard_metric?: boolean | null;
    org_target_value?: number | null;
    org_unit_of_measure?: string | null;
    override_is_critical?: boolean | null;
    override_is_dashboard_metric?: boolean | null;
    override_target_value?: number | null;
    override_unit_of_measure?: string | null;
    is_critical?: boolean | null;
    is_dashboard_metric?: boolean | null;
    is_dashboard_metric_overridden?: boolean;
    is_target_value_overridden?: boolean;
    is_unit_of_measure_overridden?: boolean;
    target_value?: number | null;
    unit_of_measure?: string | null;
    is_critical_overridden?: boolean;
    is_overridden: boolean;
    created_by?: string | null;
    created_by_name?: string;
    /** API may return numeric epoch, descriptive string (e.g. "SYSTEM SETUP"), etc. */
    created_at?: string | number | null;
    /** When `"Percentage"`, bounds are typically decimals (e.g. 0.05 = 5%). */
    value_type?: string | null;
    metrics_type?: string | null;
    first_applicable_metric_level_id?: number | null;
    first_applicable_metric_level_name?: string | null;
    first_applicable_metric_level_display_label?: string | null;
}

export interface CreateOverrideRequest {
    review_entity_id: number;
    org_threshold_id: number;
    override_lower_bound: number;
    override_upper_bound: number;
    override_reason: string;
    override_is_critical: boolean;
    override_is_dashboard_metric?: boolean | null;
    override_target_value?: number | null;
    override_unit_of_measure?: string | null;
}

export interface UpdateOverrideRequest {
    review_entity_id: number;
    org_threshold_id: number;
    override_lower_bound: number;
    override_upper_bound: number;
    override_reason: string;
    override_is_critical: boolean;
    override_is_dashboard_metric?: boolean | null;
    override_target_value?: number | null;
    override_unit_of_measure?: string | null;
}

/**
 * Get review entity metric thresholds
 * @param reviewEntityId The review entity ID
 * @returns Promise with the metrics thresholds
 */
export const getReviewEntityMetricThresholds = async (reviewEntityId: number): Promise<AxiosResponse<MetricsThreshold[]>> => {
    const endpoint = `/get_review_entity_metric_thresholds?review_entity_id=${reviewEntityId}`;
    console.log('DEBUG: Getting metrics thresholds for review entity ID:', reviewEntityId);
    
    try {
        const response = await api.get(endpoint);
        console.log('DEBUG: Metrics thresholds response:', response);
        return response;
    } catch (error) {
        console.error('DEBUG: Error getting metrics thresholds:', error);
        throw error;
    }
};

// Metric Input Metadata APIs
export interface MetricInputMetaDataEntry {
    review_entity_id: number;
    review_entity_name: string | null;
    total_metrics: number;
    earliest_metric_date: string | null;
    latest_metric_date: string | null;
    last_loaded_date: string | null;
}

export type MetricInputMetadataResponse = Record<string, MetricInputMetaDataEntry[]>;

export const getInputMetricMetadata = (): Promise<AxiosResponse<MetricInputMetadataResponse>> =>
    api.get('/get_input_metric_metadata');

export interface ComputedMetricMetadataEntry {
    review_entity_id: number;
    review_entity_name: string | null;
    metric_type: string;
    total_metrics: number;
    earliest_metric_date: string | null;
    latest_metric_date: string | null;
    last_loaded_date: string | null;
}

export type ComputedMetricMetadataResponse = Record<string, ComputedMetricMetadataEntry[]>;

export const getComputedMetricsMetadata = (): Promise<AxiosResponse<ComputedMetricMetadataResponse>> =>
    api.get('/get_computed_metrics_metadata');

/**
 * Create an override for a metrics threshold
 * @param data The override data
 * @returns Promise with the created override
 */
export const createMetricsOverride = async (data: CreateOverrideRequest): Promise<AxiosResponse<any>> => {
    const endpoint = `/create_override`;
    console.log('DEBUG: Creating metrics override with data:', data);
    
    try {
        const response = await api.post(endpoint, data);
        console.log('DEBUG: Create override response:', response);
        return response;
    } catch (error) {
        console.error('DEBUG: Error creating override:', error);
        throw error;
    }
};

/**
 * Update an existing override for a metrics threshold
 * @param data The updated override data
 * @returns Promise with the updated override
 */
export const updateMetricsOverride = async (data: UpdateOverrideRequest): Promise<AxiosResponse<any>> => {
    const endpoint = `/update_override`;
    console.log('DEBUG: Updating metrics override with data:', data);
    
    try {
        const response = await api.put(endpoint, data);
        console.log('DEBUG: Update override response:', response);
        return response;
    } catch (error) {
        console.error('DEBUG: Error updating override:', error);
        throw error;
    }
};

/**
 * Get all LLM prompt configurations
 * @returns Promise with the list of LLM prompt configurations
 */
export const getAllLLMPromptConfigs = async (): Promise<AxiosResponse<any>> => {
    const endpoint = `/llm_prompt_configs`;
    try {
        const response = await api.get(endpoint);
        return response;
    } catch (error) {
        console.error('Error fetching LLM prompt configs:', error);
        throw error;
    }
};

/**
 * Create a new LLM prompt configuration
 * @param data The LLM prompt config data
 * @returns Promise with the created config ID
 */
export const createLLMPromptConfig = async (data: {
    review_definition_id: number;
    review_entity_type_id: number;
    prompt_text: string;
}): Promise<AxiosResponse<any>> => {
    const endpoint = `/llm_prompt_config`;
    try {
        const response = await api.post(endpoint, data);
        return response;
    } catch (error) {
        console.error('Error creating LLM prompt config:', error);
        throw error;
    }
};

/**
 * Update an existing LLM prompt configuration
 * @param data The updated LLM prompt config data
 * @returns Promise with the update result
 */
export const updateLLMPromptConfig = async (data: {
    llm_prompt_config_id: number;
    review_definition_id: number;
    review_entity_type_id: number;
    prompt_text: string;
}): Promise<AxiosResponse<any>> => {
    const endpoint = `/llm_prompt_config`;
    try {
        const response = await api.put(endpoint, data);
        return response;
    } catch (error) {
        console.error('Error updating LLM prompt config:', error);
        throw error;
    }
};

/**
 * Delete an LLM prompt configuration
 * @param id The LLM prompt config ID to delete
 * @returns Promise with the delete result
 */
export const deleteLLMPromptConfig = async (id: number): Promise<AxiosResponse<any>> => {
    const endpoint = `/llm_prompt_config/${id}`;
    try {
        const response = await api.delete(endpoint);
        return response;
    } catch (error) {
        console.error('Error deleting LLM prompt config:', error);
        throw error;
    }
};

// Review Schedule Types
export interface DailyScheduleConfig {
    hour: number;
    minute: number;
}

export interface WeeklyScheduleConfig {
    day_of_week: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
    hour: number;
    minute: number;
}

export interface FortnightlyScheduleConfig {
    day_of_week: 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
    hour: number;
    minute: number;
}

export interface MonthlyScheduleConfig {
    day_of_month: number;
    hour: number;
    minute: number;
}

export interface QuarterlyScheduleConfig {
    month_in_quarter: 1 | 2 | 3;
    day_of_month: number;
    hour: number;
    minute: number;
}

export interface YearlyScheduleConfig {
    month: number;
    day_of_month: number;
    hour: number;
    minute: number;
}

export interface ReviewSchedule {
    id: number;
    review_definition_id: number;
    schedule_cron: string;
    frequency?: string;
    schedule_config?: {
        day_of_week?: string;
        day_of_month?: number;
        month_in_quarter?: number;
        month?: number;
        hour?: number;
        minute?: number;
    };
    created_at: string;
    updated_at: string;
    created_by: number;
    updated_by: number;
    review_definition?: any;
}

export interface CreateReviewSchedulePayload {
    review_definition_id: number;
    frequency: 'minutely' | 'daily' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'yearly';
    daily_config?: DailyScheduleConfig;
    weekly_config?: WeeklyScheduleConfig;
    fortnightly_config?: FortnightlyScheduleConfig;
    monthly_config?: MonthlyScheduleConfig;
    quarterly_config?: QuarterlyScheduleConfig;
    yearly_config?: YearlyScheduleConfig;
}

export interface UpdateReviewSchedulePayload extends CreateReviewSchedulePayload {
    id: number;
}

/**
 * Get all review schedules
 * @returns Promise with the list of review schedules
 */
export const getReviewSchedules = async (): Promise<AxiosResponse<ReviewSchedule[]>> => {
    const endpoint = `/review_schedules`;
    try {
        const response = await api.get(endpoint);
        return response;
    } catch (error) {
        console.error('Error fetching review schedules:', error);
        throw error;
    }
};

/**
 * Get a review schedule by ID
 * @param id The review schedule ID
 * @returns Promise with the review schedule
 */
export const getReviewScheduleById = async (id: number): Promise<AxiosResponse<ReviewSchedule>> => {
    const endpoint = `/review_schedule/${id}`;
    try {
        const response = await api.get(endpoint);
        return response;
    } catch (error) {
        console.error('Error fetching review schedule:', error);
        throw error;
    }
};

/**
 * Get a review schedule by review definition ID
 * @param reviewDefinitionId The review definition ID
 * @returns Promise with the review schedule
 */
export const getReviewScheduleByReviewDefinitionId = async (reviewDefinitionId: number): Promise<AxiosResponse<ReviewSchedule>> => {
    const endpoint = `/review_schedule_by_review_definition_id/${reviewDefinitionId}`;
    try {
        const response = await api.get(endpoint);
        return response;
    } catch (error) {
        console.error('Error fetching review schedule by review definition ID:', error);
        throw error;
    }
};

/**
 * Create a new review schedule
 * @param data The review schedule data
 * @returns Promise with the created schedule ID
 */
export const createReviewSchedule = async (data: CreateReviewSchedulePayload): Promise<AxiosResponse<number>> => {
    const endpoint = `/review_schedule`;
    try {
        const response = await api.post(endpoint, data);
        return response;
    } catch (error) {
        console.error('Error creating review schedule:', error);
        throw error;
    }
};

/**
 * Update an existing review schedule
 * @param data The updated review schedule data
 * @returns Promise with the update result
 */
export const updateReviewSchedule = async (data: UpdateReviewSchedulePayload): Promise<AxiosResponse<number>> => {
    const endpoint = `/review_schedule`;
    try {
        const response = await api.put(endpoint, data);
        return response;
    } catch (error) {
        console.error('Error updating review schedule:', error);
        throw error;
    }
};

/**
 * Delete a review schedule
 * @param id The review schedule ID to delete
 * @returns Promise with the delete result
 */
export const deleteReviewSchedule = async (id: number): Promise<AxiosResponse<string>> => {
    const endpoint = `/review_schedule/${id}`;
    try {
        const response = await api.delete(endpoint);
        return response;
    } catch (error) {
        console.error('Error deleting review schedule:', error);
        throw error;
    }
};

export const exportRaidItemDetails = () =>
  api.get('/raid_item_details/export', {
    responseType: 'blob',
  });

// Reports APIs
export interface ReviewEntityCountByType {
    review_entity_type_id: number;
    review_entity_type_name: string;
    review_entity_count: number;
}

export const getReviewEntityCountByType = (): Promise<AxiosResponse<ReviewEntityCountByType[]>> =>
    api.get('/reports/review_entity_count_by_type');

// Metrics Trending Report APIs
export interface MetricTrendingDataPoint {
    computed_metric_id: number;
    review_entity_id: number;
    review_entity_name: string;
    metric_date: string;
    review_dimension: string;
    metric_name: string;
    metric_value: number | null;
    metric_identifier_type: string;
    metric_identifier_name: string;
    created_at: string;
    updated_at: string;
}

export interface MetricsByName {
    metric_name: string;
    review_dimension: string;
    value_type?: string | null;
    unit_of_measure?: string | null;
    metrics_type?: string | null;
    org_lower_bound?: number | null;
    org_upper_bound?: number | null;
    org_target_value?: number | null;
    entity_lower_bound?: number | null;
    entity_upper_bound?: number | null;
    entity_target_value?: number | null;
    is_threshold_overridden?: boolean;
    is_target_overridden?: boolean;
    override_reason?: string | null;
    lower_bound?: number | null;
    upper_bound?: number | null;
    target_value?: number | null;
    data: {
        date: string;
        value: number | null;
        metric_identifier_type: string;
        metric_identifier_name: string;
    }[];
}

export interface HierarchyChildInfo {
    id: number;
    name: string;
    review_entity_type: string | null;
}

export interface HierarchyInfo {
    entity_id: number;
    entity_name: string;
    is_aggregate: boolean;
    direct_children_count: number;
    direct_children: HierarchyChildInfo[];
    total_descendants_count: number;
    leaf_count: number;
}

export interface MetricsTrendingResponse {
    review_entity_id: number;
    review_entity_name: string;
    review_dimension: string | null;
    is_aggregate: boolean;
    hierarchy_info: HierarchyInfo | null;
    data_points: MetricTrendingDataPoint[];
    metrics_by_name: { [key: string]: MetricsByName };
}

export const getMetricsTrending = (
    reviewEntityId: number,
    reviewDimension?: string,
    includeAggregate: boolean = true
): Promise<AxiosResponse<MetricsTrendingResponse>> => {
    const params: { review_entity_id: number; review_dimension?: string; include_aggregate: boolean } = {
        review_entity_id: reviewEntityId,
        include_aggregate: includeAggregate
    };
    if (reviewDimension) {
        params.review_dimension = reviewDimension;
    }
    return api.get('/reports/metrics_trending', { params });
};

/** Pass reviewEntityId to restrict to dimensions applicable to that entity (entity attribute or entity type). */
export const getAvailableDimensionsForMetrics = (
    reviewEntityId?: number,
): Promise<AxiosResponse<string[]>> =>
    api.get('/reports/metrics_trending/dimensions', {
        params:
            reviewEntityId != null && reviewEntityId > 0
                ? { review_entity_id: reviewEntityId }
                : {},
    });

export const getMetricNamesForDimension = (
    reviewDimension: string
): Promise<AxiosResponse<string[]>> =>
    api.get('/reports/metrics_trending/metric_names', {
        params: { review_dimension: reviewDimension }
    });

export const getEntityHierarchy = (
    reviewEntityId: number
): Promise<AxiosResponse<HierarchyInfo>> =>
    api.get('/reports/metrics_trending/hierarchy', {
        params: { review_entity_id: reviewEntityId }
    });

// Comparison view types
export interface ComparisonSeries {
    entity_name: string;
    data: (number | null)[];
}

export interface ComparisonMetricData {
    metric_name: string;
    review_dimension: string;
    value_type?: string | null;
    unit_of_measure?: string | null;
    metrics_type?: string | null;
    org_lower_bound?: number | null;
    org_upper_bound?: number | null;
    org_target_value?: number | null;
    entity_lower_bound?: number | null;
    entity_upper_bound?: number | null;
    entity_target_value?: number | null;
    is_threshold_overridden?: boolean;
    is_target_overridden?: boolean;
    override_reason?: string | null;
    lower_bound?: number | null;
    upper_bound?: number | null;
    target_value?: number | null;
    dates: string[];
    series: ComparisonSeries[];
}

export interface ChildEntityData {
    entity_id: number;
    entity_name: string;
    entity_type: string | null;
    is_aggregate: boolean;
    data_points: MetricTrendingDataPoint[];
}

export interface ChildrenComparisonResponse {
    parent_entity_id: number;
    parent_entity_name: string;
    review_dimension: string | null;
    metric_name: string | null;
    children_count: number;
    children_data: ChildEntityData[];
    comparison_by_metric: { [key: string]: ComparisonMetricData };
    available_metrics: string[];
}

export const getChildrenComparison = (
    parentEntityId: number,
    reviewDimension?: string,
    metricName?: string
): Promise<AxiosResponse<ChildrenComparisonResponse>> => {
    const params: { parent_entity_id: number; review_dimension?: string; metric_name?: string } = {
        parent_entity_id: parentEntityId
    };
    if (reviewDimension) {
        params.review_dimension = reviewDimension;
    }
    if (metricName) {
        params.metric_name = metricName;
    }
    return api.get('/reports/metrics_trending/compare', { params });
};

// ============================================
// GATED REVIEWS (attached to review_entity)
// ============================================

export interface GatedReview {
    name: string;
    schedule_cron: string;  // Cron expression for the scheduled date
    gate_name?: string | null;  // Gate/milestone name
    review_definition_id?: number | null;  // Linked gated review definition
    added_by?: number | null;
    created_at?: string | null;
}

// ============================================
// DASHBOARD - USER RELATED ENTITIES
// ============================================

export interface UserRelatedEntity {
    id: number;
    name: string;
    description: string | null;
    parents: number[] | null;
    review_entity_type_id: number;
    entity_type_name: string;
    entity_type_display_label: string;
    entity_type_category: string | null;
    owner: number | null;
    reviewers: number[] | null;
    reviewees: number[] | null;
    status: string;
    valid_from: string | null;
    valid_till: string | null;
    org_entity_code: string | null;
    relation_type: 'direct' | 'child';
    depth: number;
}

export interface UserDirectEntity {
    id: number;
    name: string;
    description: string | null;
    parents: number[] | null;
    review_entity_type_id: number;
    entity_type_name: string;
    entity_type_display_label: string;
    entity_type_category: string | null;
    owner: number | null;
    reviewers: number[] | null;
    reviewees: number[] | null;
    status: string;
    valid_from: string | null;
    valid_till: string | null;
    org_entity_code: string | null;
    user_role: 'owner' | 'reviewer' | 'reviewee';
}

export interface EntityCountByCategory {
    category: string;
    entity_type: string;
    count: number;
}

export interface UserRelatedEntitiesResponse {
    entities: UserRelatedEntity[];
    direct_entities: UserRelatedEntity[];
    child_entities: UserRelatedEntity[];
    counts_by_category: EntityCountByCategory[];
    hierarchy: Record<string, UserRelatedEntity[]>;
    summary: {
        total_entities: number;
        direct_count: number;
        child_count: number;
        categories: string[];
    };
}

export interface UserDirectEntitiesResponse {
    entities: UserDirectEntity[];
    by_role: {
        owner: UserDirectEntity[];
        reviewer: UserDirectEntity[];
        reviewee: UserDirectEntity[];
    };
    by_category: Record<string, UserDirectEntity[]>;
    summary: {
        total: number;
        as_owner: number;
        as_reviewer: number;
        as_reviewee: number;
    };
}

export interface UserDashboardSummaryResponse {
    user_id: number;
    direct_entities: {
        total: number;
        as_owner: number;
        as_reviewer: number;
        as_reviewee: number;
    };
    total_entities_with_children: number;
    counts_by_category: EntityCountByCategory[];
    entities_by_role: {
        owner: UserDirectEntity[];
        reviewer: UserDirectEntity[];
        reviewee: UserDirectEntity[];
    };
}

export const getUserRelatedEntities = (): Promise<AxiosResponse<UserRelatedEntitiesResponse>> => {
    return api.get('/dashboard/user/related-entities');
};

export const getUserDirectEntities = (): Promise<AxiosResponse<UserDirectEntitiesResponse>> => {
    return api.get('/dashboard/user/direct-entities');
};

export const getUserDashboardSummary = (): Promise<AxiosResponse<UserDashboardSummaryResponse>> => {
    return api.get('/dashboard/user/summary');
};

// ============================================
// DASHBOARD - ENTITY HIGH-LEVEL INFO
// ============================================

export interface EntityUserInfo {
    id: number;
    username: string;
    display_name: string;
}

export interface EntityLastReview {
    id: number;
    name: string;
    date: string;
    status: string;
}

export interface EntityRaidByCategory {
    raid_category: string;
    count: number;
}

export interface EntityParentInfo {
    id: number;
    name: string;
    entity_type_name: string;
    entity_type_display_label: string;
}

export interface EntityHighLevelInfoResponse {
    entity: {
        id: number;
        name: string;
        description: string | null;
        status: string;
        org_entity_code: string | null;
        valid_from: string | null;
        valid_till: string | null;
        entity_type_name: string;
        entity_type_display_label: string;
        entity_type_category: string | null;
        parents: EntityParentInfo[];
    };
    owner: EntityUserInfo | null;
    reviewers: EntityUserInfo[];
    reviewees: EntityUserInfo[];
    last_review: EntityLastReview | null;
    last_metric_date: string | null;
    open_raid_count: number;
    open_raid_by_category: EntityRaidByCategory[];
    child_count: number;
}

export const getEntityHighLevelInfo = (entityId: number): Promise<AxiosResponse<EntityHighLevelInfoResponse>> => {
    return api.get('/dashboard/entity/high-level-info', { params: { entity_id: entityId } });
};

// ============================================
// DASHBOARD - ENTITY DEMOGRAPHICS
// ============================================

export interface AttributeDistribution {
    display_label: string;
    data_type: string;
    distribution: Record<string, number>;
}

export interface EntityDemographicsResponse {
    is_leaf: boolean;
    entity_count: number;
    demographics: Record<string, AttributeDistribution>;
}

export const getEntityDemographics = (entityId: number): Promise<AxiosResponse<EntityDemographicsResponse>> => {
    return api.get('/dashboard/entity/attribute-demographics', { params: { entity_id: entityId } });
};

// ============================================
// DASHBOARD - CATEGORY-BASED NAVIGATION
// ============================================

export interface CategoryWithCount {
    category: string;
    entity_count: number;
    min_depth: number;
}

export interface ChildCategoriesWithCountsResponse {
    categories: CategoryWithCount[];
}

export const getChildCategoriesWithCounts = (parentEntityId: number): Promise<AxiosResponse<ChildCategoriesWithCountsResponse>> => {
    return api.get('/dashboard/child-categories-with-counts', { params: { parent_entity_id: parentEntityId } });
};

export interface CategoryChildEntity {
    id: number;
    name: string;
    description: string;
    entity_type_id: number;
    entity_type_name: string;
    entity_type_display_label: string;
    category: string;
}

export interface ChildrenByCategoryResponse {
    entities: CategoryChildEntity[];
}

export const getChildrenByCategory = (parentEntityId: number, category: string): Promise<AxiosResponse<ChildrenByCategoryResponse>> => {
    return api.get('/dashboard/children-by-category', { params: { parent_entity_id: parentEntityId, category } });
};

export const getDescendantsByCategory = (parentEntityId: number, category: string): Promise<AxiosResponse<ChildrenByCategoryResponse>> => {
    return api.get('/dashboard/descendants-by-category', { params: { parent_entity_id: parentEntityId, category } });
};

export interface MetricDistributionBin {
    label: string;
    count: number;
    count_pct: number;
    /** Project names whose latest metric value falls in this bin */
    project_names?: string[];
}

export interface PortfolioMetricDistribution {
    metric_name: string;
    value_type: string | null;
    bins: MetricDistributionBin[];
    project_count: number;
    org_threshold_lower_bound?: number | null;
    org_threshold_upper_bound?: number | null;
    org_lower_bound?: number | null;
    org_upper_bound?: number | null;
    lower_bound?: number | null;
    upper_bound?: number | null;
}

export interface PortfolioDimensionGroup {
    review_dimension: string;
    metrics: PortfolioMetricDistribution[];
}

export interface ProjectTypeMetricGroup {
    entity_type_id: number;
    entity_type_name: string;
    display_label: string;
    dimensions: PortfolioDimensionGroup[];
}

export interface ProjectMetricsDistributionResponse {
    parent_entity_id: number;
    parent_entity_name: string;
    project_type_groups: ProjectTypeMetricGroup[];
}

export const getProjectMetricsDistribution = (
    parentEntityId: number
): Promise<AxiosResponse<ProjectMetricsDistributionResponse>> => {
    return api.get('/dashboard/project-metrics-distribution', {
        params: { parent_entity_id: parentEntityId }
    });
};

export interface LeafProjectEntityOption {
    id: number;
    name: string;
    entity_type_display_label: string | null;
    entity_type_name: string | null;
}

export interface LeafProjectEntitiesResponse {
    parent_entity_id: number;
    parent_entity_name: string;
    entities: LeafProjectEntityOption[];
}

export const getLeafProjectEntities = (
    parentEntityId: number
): Promise<AxiosResponse<LeafProjectEntitiesResponse>> => {
    return api.get('/dashboard/leaf-project-entities', {
        params: { parent_entity_id: parentEntityId }
    });
};

// ============================================
// DASHBOARD - DEMOGRAPHICS BY CATEGORY
// ============================================

export interface CategoryEntityDemographics {
    id: number;
    name: string;
    entity_type_display_label: string;
    entity_count: number;
    demographics: Record<string, AttributeDistribution & { total?: number; count?: number; average?: number }>;
}

export interface DemographicsByCategoryResponse {
    parent_entity_id: number;
    category: string;
    category_entities: CategoryEntityDemographics[];
    aggregated_demographics: Record<string, AttributeDistribution & { total?: number; count?: number; average?: number }>;
    total_entity_count: number;
}

export const getDemographicsByCategory = (
    parentEntityId: number, 
    category: string
): Promise<AxiosResponse<DemographicsByCategoryResponse>> => {
    return api.get('/dashboard/demographics-by-category', { 
        params: { parent_entity_id: parentEntityId, category } 
    });
};

// ============================================
// DASHBOARD - DEMOGRAPHICS (review_entity aggregates)
// ============================================

export interface DashboardDemographicsLeafAttribute {
    name: string;
    display_label: string;
    data_type: string;
    value: unknown;
    description?: string;
}

/** Review entity reference for demographics tooltips / drill-down */
export interface DemographicsEntityRef {
    review_entity_id: number;
    display_label: string;
}

/** Aggregate payload shapes from backend DashboardDemographicsOps */
export type DashboardDemographicsAggregate = Record<string, unknown>;

export interface DashboardDemographicsGroup {
    entity_count: number;
    entity_names: string[];
    attributes: Array<{
        name: string;
        display_label: string;
        data_type: string;
        sample_size: number;
        aggregate: DashboardDemographicsAggregate;
    }>;
}

export interface DashboardDemographicsResponse {
    error?: string;
    entity_id?: number;
    entity_name?: string;
    entity_type?: string | null;
    category?: string | null;
    is_leaf?: boolean;
    target_category?: string;
    total_matching_entities?: number;
    attributes?: DashboardDemographicsLeafAttribute[];
    groups?: Record<string, DashboardDemographicsGroup>;
}

export const getDashboardDemographics = (
    entityId: number,
    category: string,
): Promise<AxiosResponse<DashboardDemographicsResponse>> => {
    return api.get(`/dashboard/demographics/${entityId}`, {
        params: { category },
    });
};

export default api;